// The rail's editor: everything the rail can hold, each with its switch, in
// the rail's own order. A row moves within its group by its handle or by its
// arrows. What is chosen is kept in this browser (state/rail.ts).

import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, GripVertical } from "lucide-react";
import { defaultRail, groupOf, isFixed, moveOnRail, RAIL_GROUPS, resetRail, sameRail, showOnRail, useRail, type RailId, type RailSlot } from "../state/rail";
import { GROUP_LABEL, RAIL_ITEMS } from "./railItems";
import { clamp, cx } from "../lib/util";
import { useScrollEnds } from "./ui";

interface Drag {
  id: RailId;
  from: number;
  to: number;
  n: number;
  y0: number;
  list: HTMLElement;
  /** where the list was when the drag began, so scrolling while dragging counts */
  top0: number;
  dy: number;
  /** a row and the gap after it */
  h: number;
}

const reduced = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export function RailEditor({ anchor, onClose }: { anchor: HTMLElement | null; onClose: (refocus: boolean) => void }) {
  const slots = useRail((r) => r.slots);
  const ref = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  dragRef.current = drag;
  const pointerY = useRef(0);
  const changed = !sameRail(slots, defaultRail());
  const ends = useScrollEnds(body);

  // a row that moves glides to its new place from where it was drawn, a dropped one too
  const before = useRef(new Map<string, number>());
  const remember = () => {
    before.current.clear();
    for (const li of body.current?.querySelectorAll<HTMLElement>("li[data-id]") ?? []) before.current.set(li.dataset.id!, li.getBoundingClientRect().top);
  };
  useLayoutEffect(() => {
    const was = before.current;
    if (!was.size) return;
    before.current = new Map();
    for (const li of body.current?.querySelectorAll<HTMLElement>("li[data-id]") ?? []) {
      const top = was.get(li.dataset.id!);
      const d = top === undefined ? 0 : top - li.getBoundingClientRect().top;
      if (Math.abs(d) < 1) continue;
      if (li.contains(document.activeElement)) li.scrollIntoView({ block: "nearest" });
      if (!reduced()) li.animate([{ transform: `translateY(${d}px)` }, { transform: "none" }], { duration: 200, easing: "cubic-bezier(0.2, 0.9, 0.3, 1)" });
    }
  });

  // beside the rail, its foot level with the rail's, and never off the screen
  useLayoutEffect(() => {
    const place = () => {
      const el = ref.current;
      if (!el) return;
      const vh = window.innerHeight;
      el.style.maxHeight = `${vh - 28}px`;
      const rail = anchor?.closest(".rail")?.getBoundingClientRect();
      const h = el.offsetHeight;
      el.style.left = `${(rail?.right ?? 62) + 10}px`;
      el.style.top = `${clamp((rail?.bottom ?? vh / 2 + h / 2) - h, 14, vh - 14 - h)}px`;
    };
    place();
    ref.current?.focus({ preventScroll: true });
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [anchor]);

  useEffect(() => {
    const down = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !anchor?.contains(t)) onClose(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      // Esc first puts a row being dragged back where it was
      if (dragRef.current) {
        remember();
        return setDrag(null);
      }
      onClose(ref.current?.contains(document.activeElement) ?? false);
    };
    window.addEventListener("pointerdown", down, true);
    window.addEventListener("keydown", key, true);
    return () => {
      window.removeEventListener("pointerdown", down, true);
      window.removeEventListener("keydown", key, true);
    };
  }, [anchor, onClose]);

  const move = (id: RailId, to: number) => {
    remember();
    moveOnRail(id, to);
  };

  const follow = (d: Drag, y: number) => {
    const dy = clamp(y - d.y0 + (d.top0 - d.list.getBoundingClientRect().top), -d.from * d.h, (d.n - 1 - d.from) * d.h);
    setDrag({ ...d, dy, to: clamp(Math.round(d.from + dy / d.h), 0, d.n - 1) });
  };

  const lift = (e: React.PointerEvent<HTMLElement>, id: RailId, from: number, n: number) => {
    if (e.button !== 0 || n < 2) return;
    e.preventDefault();
    const li = e.currentTarget.closest("li")!;
    const list = li.parentElement!;
    // rows still gliding from a move land at once, so the one in hand follows the pointer
    for (const a of body.current?.getAnimations({ subtree: true }) ?? []) a.finish();
    e.currentTarget.setPointerCapture(e.pointerId);
    pointerY.current = e.clientY;
    setDrag({ id, from, to: from, n, y0: e.clientY, list, top0: list.getBoundingClientRect().top, dy: 0, h: li.offsetHeight + (parseFloat(getComputedStyle(list).rowGap) || 0) });
  };

  const drop = () => {
    const d = dragRef.current;
    if (!d) return;
    remember();
    setDrag(null);
    if (d.to !== d.from) moveOnRail(d.id, d.to);
  };

  // near the top or the foot of a list that scrolls, the list scrolls to meet the row
  const dragging = !!drag;
  useEffect(() => {
    if (!dragging) return;
    let raf = 0;
    const tick = () => {
      const b = body.current;
      const d = dragRef.current;
      if (b && d) {
        const r = b.getBoundingClientRect();
        const y = pointerY.current;
        const v = y < r.top + 32 ? -(r.top + 32 - y) / 4 : y > r.bottom - 32 ? (y - r.bottom + 32) / 4 : 0;
        const was = b.scrollTop;
        if (v) b.scrollTop += v;
        if (b.scrollTop !== was) follow(d, y);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [dragging]);

  const shift = (s: RailSlot, i: number): number => {
    if (!drag || groupOf(s.id) !== groupOf(drag.id)) return 0;
    if (s.id === drag.id) return drag.dy;
    if (drag.from < drag.to && i > drag.from && i <= drag.to) return -drag.h;
    if (drag.from > drag.to && i >= drag.to && i < drag.from) return drag.h;
    return 0;
  };

  return (
    <div
      ref={ref}
      className={cx("rail-editor island", drag && "is-dragging")}
      role="dialog"
      aria-label="Customize toolbar"
      tabIndex={-1}
      onKeyDown={(e) => {
        // Space and Enter press the switches here, and the arrows never nudge a widget behind
        if ([" ", "Enter", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) e.stopPropagation();
      }}
    >
      <div className="rail-editor-head">
        <div>
          <h3>Customize toolbar</h3>
          <p>Drag a row to move it.</p>
        </div>
        <button
          className="btn btn-xs btn-ghost"
          disabled={!changed}
          onClick={() => {
            remember();
            resetRail();
          }}
        >
          Reset
        </button>
      </div>
      <div ref={body} className={cx("rail-editor-body", ends)}>
        {RAIL_GROUPS.map((g) => {
          const rows = slots.filter((s) => groupOf(s.id) === g);
          return (
            <section key={g}>
              <h4 id={`rail-group-${g}`}>{GROUP_LABEL[g]}</h4>
              <ul aria-labelledby={`rail-group-${g}`}>
                {rows.map((s, i) => (
                  <Row
                    key={s.id}
                    slot={s}
                    first={i === 0}
                    last={i === rows.length - 1}
                    lifted={drag?.id === s.id}
                    shift={shift(s, i)}
                    onUp={() => move(s.id, i - 1)}
                    onDown={() => move(s.id, i + 1)}
                    grip={{
                      onPointerDown: (e) => lift(e, s.id, i, rows.length),
                      onPointerMove: (e) => {
                        pointerY.current = e.clientY;
                        if (dragRef.current) follow(dragRef.current, e.clientY);
                      },
                      onPointerUp: drop,
                      onPointerCancel: () => setDrag(null),
                    }}
                  />
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function Row({ slot, first, last, lifted, shift, onUp, onDown, grip }: { slot: RailSlot; first: boolean; last: boolean; lifted: boolean; shift: number; onUp: () => void; onDown: () => void; grip: React.HTMLAttributes<HTMLElement> }) {
  const it = RAIL_ITEMS[slot.id];
  const fixed = isFixed(slot.id);
  const id = useId();
  return (
    <li data-id={slot.id} className={cx("rail-row", !slot.on && "is-off", lifted && "is-lifted")} style={shift ? { transform: `translateY(${shift}px)` } : undefined}>
      <span className="rail-grip" aria-hidden {...grip}>
        <GripVertical size={13} />
      </span>
      <label className="rail-row-main" htmlFor={id}>
        <span className="rail-row-ico">{it.icon(15)}</span>
        <span className="rail-row-label">{it.label}</span>
      </label>
      {/* its key, and in the same place while the row is pointed at or focused, its arrows */}
      <span className="rail-row-end">
        {it.kbd && (
          <kbd className="rail-row-kbd" aria-hidden>
            {it.kbd}
          </kbd>
        )}
        <span className="rail-row-arrows">
          <button aria-label={`Move ${it.label} up`} aria-disabled={first} onClick={() => !first && onUp()}>
            <ChevronUp size={13} />
          </button>
          <button aria-label={`Move ${it.label} down`} aria-disabled={last} onClick={() => !last && onDown()}>
            <ChevronDown size={13} />
          </button>
        </span>
      </span>
      <button id={id} role="switch" aria-checked={slot.on} aria-label={it.label} aria-disabled={fixed || undefined} className={cx("switch", slot.on && "is-on")} title={fixed ? "Always on the toolbar" : undefined} onClick={() => !fixed && showOnRail(slot.id, !slot.on)}>
        <i />
      </button>
    </li>
  );
}
